// src/features/admin/roleMappingApi.ts
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';

// Admin token refresh utility
const adminRefreshToken = async () => {
  const email = localStorage.getItem('admin_email');
  const password = localStorage.getItem('admin_password');
  
  if (!email || !password) return null;
  
  try {
    const response = await fetch(`${process.env.REACT_APP_SERVER_ENDPOINT}/admin/login`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'authkey': '7b4e1fd03f57885c1ff900ad76ea3810bb7160c7157a4c31cf2de3',
      },
      body: JSON.stringify({
        email: email.trim(),
        password: password,
      }),
    });
    
    const data = await response.json();
    
    if (response.ok && data.token) {
      localStorage.setItem('admin_token', data.token);
      return data.token;
    }
    
    return null;
  } catch (err) {
    console.error('Admin token refresh failed:', err);
    return null;
  }
};

const BASE_URL = process.env.REACT_APP_SERVER_ENDPOINT;
const AUTH_KEY = '7b4e1fd03f57885c1ff900ad76ea3810bb7160c7157a4c31cf2de3';

// Permission structure
export interface Permission {
  menu_id: string;
  menu_name: string;
  can_read: boolean;
  can_write: boolean;
}

// Role mapping structure - Updated to match database
export interface RoleMapping {
  id: number;
  role_id: number;
  role_name: string;
  admin_type: 'super_admin' | 'admin' | 'operation';
  module_or_screen: string;
  rights: string;
  createddt?: string;
  updateddt?: string | null;
  created_at?: string;
  updated_at?: string | null;
}

// Request/Response types
export interface CreateRoleMappingRequest {
  role_id: number;
  admin_type?: 'super_admin' | 'admin' | 'operation';
  module_or_screen: string;
  rights: string;
}

export interface UpdateRoleMappingRequest {
  recid: number;
  role_id: number;
  admin_type?: 'super_admin' | 'admin' | 'operation';
  module_or_screen: string;
  rights: string;
}

export interface DeleteRoleMappingRequest {
  recid: number;
}

export interface RoleMappingResponse {
  error: boolean;
  message: string;
  data?: RoleMapping;
  roleMappings?: RoleMapping[];
  role_mappings?: RoleMapping[];
  role_detail?: RoleMapping[];
  recId?: number;
}

// Available menu items in the system
export const MENU_ITEMS = [
  { id: 'dashboard', name: 'Dashboard' },
  { id: 'user_list', name: 'User List' },
  { id: 'product_management', name: 'Product Management' },
  { id: 'category_management', name: 'Category Management' },
  { id: 'unit_management', name: 'Unit Management' },
  { id: 'order_management', name: 'Order Management' },
  { id: 'warranty_management', name: 'Warranty Management' },
  { id: 'subcategory_management', name: 'Sub Category Management' },
  { id: 'delivery_agent_management', name: 'Delivery Agent Management' },
  { id: 'warehouse_management', name: 'Warehouse Management' },
  { id: 'banner_management', name: 'Banner Management' },
  { id: 'subbanner_management', name: 'Sub Banner Management' },
  { id: 'review', name: 'Review' },
  { id: 'guest_order_list', name: 'Guest User Details' },
  { id: 'guest_user_order', name: 'Guest Users Order List' },
  { id: 'set_credit', name: 'Set Credit for User' },
  { id: 'product_warehouse_management', name: 'Product Warehouse Management' },
  { id: 'deal_of_the_day', name: 'Deal of the Day' },
  { id: 'coupon_management', name: 'Coupon Management' },
  { id: 'admin_coupon_users', name: 'Coupon Admin Management' },
  { id: 'reward_management', name: 'Reward Management' },
  { id: 'reward_settings_management', name: 'Reward Settings Management' },
  { id: 'admin_rewards', name: 'Admin Reward Management' },
  { id: 'analytics', name: 'Report & Analytics' },
  { id: 'flash_sale', name: 'Flash Sale' },
  { id: 'delivery_management', name: 'Delivery Management' },
  { id: 'shipping_management', name: 'Shipping Management' },
  { id: 'delivery_settings', name: 'Delivery Settings' },
  { id: 'admin_users', name: 'Admin Users' },
  { id: 'settings', name: 'Settings' },
];

export const roleMappingApi = createApi({
  reducerPath: 'roleMappingApi',
  baseQuery: fetchBaseQuery({
    baseUrl: BASE_URL + '/admin',
    prepareHeaders: async (headers) => {
      let token = localStorage.getItem('admin_token');
      
      if (!token) {
        token = await adminRefreshToken();
      }
      
      if (token) {
        headers.set('Authorization', `Bearer ${token}`);
      }
      headers.set('authkey', AUTH_KEY);
      headers.set('Content-Type', 'application/json');
      
      return headers;
    },
  }),
  tagTypes: ['RoleMapping'],
  endpoints: (builder) => ({
    // List all role mappings
    listRoleMappings: builder.query<RoleMappingResponse, void>({
      query: () => ({
        url: '/list_role_mappings',
        method: 'POST',
      }),
      providesTags: ['RoleMapping'],
      transformResponse: (response: any) => {
        console.log('Raw list_role_mappings API response:', response);
        return {
          error: response.error || false,
          message: response.message || '',
          role_mappings: response.role_mappings || []
        };
      },
    }),

    // Create role mapping
    createRoleMapping: builder.mutation<RoleMappingResponse, CreateRoleMappingRequest>({
      query: (data) => ({
        url: '/add_role_detail',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['RoleMapping'],
      transformResponse: (response: any) => {
        console.log('Create role mapping response:', response);
        return response;
      },
    }),

    // Update role mapping
    updateRoleMapping: builder.mutation<RoleMappingResponse, UpdateRoleMappingRequest>({
      query: (data) => ({
        url: '/update_role_detail',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['RoleMapping'],
    }),

    // Delete role mapping
    deleteRoleMapping: builder.mutation<RoleMappingResponse, number>({
      query: (id) => ({
        url: '/delete_role_detail',
        method: 'POST',
        body: { recid: id },
      }),
      invalidatesTags: ['RoleMapping'],
    }),

    // Get role mapping by role ID
    getRoleMappingByRoleId: builder.query<RoleMappingResponse, number>({
      query: (role_id) => ({
        url: '/list_role_detail',
        method: 'POST',
        body: { role_id },
      }),
      transformResponse: (response: any) => {
        console.log('Role detail API response:', response);
        return {
          error: response.error || false,
          message: response.message || '',
          role_detail: response.role_detail || []
        };
      },
    }),
  }),
});

export const {
  useListRoleMappingsQuery,
  useCreateRoleMappingMutation,
  useUpdateRoleMappingMutation,
  useDeleteRoleMappingMutation,
  useGetRoleMappingByRoleIdQuery,
} = roleMappingApi;